
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@supabase/auth-helpers-react";
import { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const formSchema = z.object({
  churchName: z.string().min(2, { message: "Church name is required" }),
  churchCity: z.string().min(2, { message: "Church city is required" }),
  contactName: z.string().min(2, { message: "Clergy contact name is required" }),
  contactPhone: z.string()
    .min(10, { message: "Phone number must be at least 10 digits" })
    .refine((val) => /^[0-9()-.\s]+$/.test(val), { 
      message: "Phone number can only contain digits, parentheses, hyphens and spaces" 
    }),
  additionalInfo: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function ChurchSponsorForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const session = useSession();

  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Strip all non-numeric characters
    const numbers = value.replace(/\D/g, "");
    
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      churchName: "",
      churchCity: "",
      contactName: "",
      contactPhone: "",
      additionalInfo: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        title: "Church Sponsorship Request",
        name: data.contactName,
        phone: data.contactPhone,
        organization: `${data.churchName} - ${data.churchCity}`,
        email: session?.user?.email || "",
        notes: data.additionalInfo || "",
        source: "Grace Awakens App"
      };

      console.log("Sending data to webhook:", payload);
      
      const response = await fetch("https://hook.us2.make.com/aw20sjd6cskqt5qjcqf2sp14fuacdz9t", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error("Webhook error response:", await response.text());
        throw new Error("Failed to submit form");
      }

      toast({
        title: "Sponsorship Request Submitted",
        description: "We've received your church sponsorship request. Our team will contact you soon with details.",
      });
      
      setIsSuccess(true);
      form.reset();
    } catch (error) {
      console.error("Error submitting church sponsor form:", error);
      toast({
        title: "Submission Failed",
        description: "There was a problem submitting your request. Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-app-surface border border-app-border rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold mb-4 text-app-accent">Sponsor Your Church</h2>
      <p className="text-app-text-secondary mb-6">
        Get a 30% discount on group packages for your church community. Fill out the form below and our team will contact you with details.
      </p>
      
      {isSuccess ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="bg-green-100 rounded-full p-3 mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium mb-2">Request Submitted!</h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            Thank you for your interest in sponsoring your church. Our team will contact you shortly.
          </p>
          <Button
            variant="outline"
            onClick={() => setIsSuccess(false)}
            className="mt-2"
          >
            Submit Another Request
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="churchName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Church Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your church's name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="churchCity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Church City</FormLabel>
                  <FormControl>
                    <Input placeholder="City where your church is located" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Clergy Contact</FormLabel>
                  <FormControl>
                    <Input placeholder="Name of person who works at your church" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contactPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Phone</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="(555) 123-4567" 
                      type="tel" 
                      {...field}
                      onChange={(e) => {
                        // Format the phone number when the user types
                        const formatted = formatPhoneNumber(e.target.value);
                        field.onChange(formatted);
                      }} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="additionalInfo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Information (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Tell us more about your church needs" 
                      className="min-h-[100px]" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              className="w-full mt-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Request Church Sponsorship"
              )}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
